/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import * as React from "react";
import { Helmet } from "react-helmet-async";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RecaptchaProvider } from "app/components/recaptcha/recaptcha-provider";
import { recaptchaSiteKey } from "utils/api/endpoints";
import { isLocalDevHost } from "utils/constants";
import {
  installExternalAnchorRedirectGuard,
  redirectToAbsoluteUrl,
} from "utils/external-redirect";
import { useGlobalSlice } from "./slice";
import { ThemeProvider } from "./components/ui/theme-provider";
import { Toaster } from "./components/ui/toaster";
import { TooltipProvider } from "./components/ui/tooltip";
import { DevAccessGateDialog } from "./components/dev-access-gate-dialog";

import { AdminLayout } from "./components/AdminLayout";
import { TawkToHandler } from "./components/tawk-to-handler";
import { Login } from "./pages/Customer/Auth/Login/Loadable";
import { ForgotPassword } from "./pages/Customer/Auth/ForgotPassword/Loadable";
import { AuthExchange } from "./pages/Customer/Auth/AuthExchange/Loadable";
import { Home } from "./pages/Customer/Landing/Home/Loadable";
import { PlatformDrivers } from "./pages/Customer/Landing/PlatformDrivers/Loadable";
import { PlansAndPricing } from "./pages/Customer/Landing/PlansAndPricing/Loadable";
import { HowToSwitch } from "./pages/Customer/Landing/HowToSwitch/Loadable";
import { AboutVLEMobile } from "./pages/Customer/Landing/AboutVLEMobile/Loadable";
import { ScheduleCall } from "./pages/Customer/Landing/ScheduleCall/Loadable";
import { NetworkCoverage } from "./pages/Customer/Landing/NetworkCoverage/Loadable";
import { InternationalCalls } from "./pages/Customer/Landing/InternationalCalls/Loadable";
import { SupportCenter } from "./pages/Customer/Landing/SupportCenter/Loadable";
import { IMEICheck } from "./pages/Customer/Landing/IMEICheck/Loadable";
import { AbouteSIM } from "./pages/Customer/Landing/AbouteSIM/Loadable";
import { HowVLEMobileWorks } from "./pages/Customer/Landing/HowVLEMobileWorks/Loadable";
import { FAQ } from "./pages/Customer/Landing/FAQ/Loadable";
import { ResourceCenter } from "./pages/Customer/Landing/ResourceCenter/Loadable";
import { Blog } from "./pages/Customer/Landing/Blog/Loadable";
import { BlogDetail } from "./pages/Customer/Landing/BlogDetail/Loadable";
import { WhyChooseVLEMobile } from "./pages/Customer/Landing/WhyChooseVLEMobile/Loadable";
import { FixedPhone } from "./pages/Customer/FixedPhone/Loadable";
import { ListPage } from "./pages/Customer/FixedPhone/ListPage/Loadable";
import { PhoneBase } from "./pages/Customer/FixedPhone/PhoneBase/Loadable";
import { DataTopup } from "./pages/Customer/Landing/DataTopup/Loadable";
import { FamilyPlans } from "./pages/Customer/Landing/FamilyPlans/Loadable";
import { BroadbandFacts } from "./pages/Customer/Landing/BroadbandFacts/Loadable";
import { DigitalSecurity } from "./pages/Customer/Landing/DigitalSecurity/Loadable";
import { DigitalSecuritySelection } from "./pages/Customer/Landing/DigitalSecurity/DigitalSecuritySelection/Loadable";
import { SIMSwap } from "./pages/Customer/Landing/SIMSwap/Loadable";
import { NetworkPlan } from "./pages/Customer/NetworkPlanPurchase/NetworkPlan/Loadable";
import { Activation } from "./pages/Customer/Activation/Loadable";
import { FixedPhoneFlow } from "./pages/Customer/Activation/FixedPhoneFlow/Loadable";
import { ActivationCode } from "./pages/Customer/Activation/FixedPhoneFlow/components/activation-code";
import { ChooseServicePlan } from "./pages/Customer/Activation/FixedPhoneFlow/components/choose-service-plan";
import { SecurityPlanFreeTrial } from "./pages/Customer/Activation/FixedPhoneFlow/components/security-plan-free-trial";
import { OneTimeOffer } from "./pages/Customer/Activation/FixedPhoneFlow/components/one-time-offer";
import { PreloadedSimFlow } from "./pages/Customer/Activation/PreloadedSimFlow/Loadable";
import { DeviceInformation } from "./pages/Customer/Activation/PreloadedSimFlow/components/device-information";
import { ChooseServicePlan as SimCardUpsellChooseServicePlan } from "./pages/Customer/Activation/PreloadedSimFlow/components/choose-service-plan";
import { SecurityPlanUpgrade } from "./pages/Customer/Activation/PreloadedSimFlow/components/security-plan-upgrade";
import { QuickRecharge } from "./pages/Customer/Auth/QuickRecharge/Loadable";
import { SupportTickets } from "./pages/Customer/Support/SupportTickets/Loadable";
import { SubmitTicket } from "./pages/Customer/Support/SubmitTicket/Loadable";
import { ManageTicket } from "./pages/Customer/Support/ManageTicket/Loadable";
import { Disclosure } from "./pages/Customer/Legal/Disclosure/Loadable";
import { NetworkPolicy } from "./pages/Customer/Legal/NetworkPolicy/Loadable";
import { Accessibility } from "./pages/Customer/Legal/Accessibility/Loadable";
import { ReturnPolicy } from "./pages/Customer/Legal/ReturnPolicy/Loadable";
import { TermsAndConditions } from "./pages/Customer/Legal/TermsAndConditions/Loadable";
import { PrivacyPolicy } from "./pages/Customer/Legal/PrivacyPolicy/Loadable";
import { DeletionPolicy } from "./pages/Customer/Legal/DeletionPolicy/Loadable";
import { DoNotSellMyData } from "./pages/Customer/Legal/DoNotSellMyData/Loadable";
import { SingleNetworkSelection } from "./pages/Customer/NetworkPlanPurchase/SingleNetworkSelection/Loadable";
import { MakeASelection } from "./pages/Customer/NetworkPlanPurchase/SingleNetworkSelection/components/make-a-selection";
import { DeviceCompatibilty } from "./pages/Customer/NetworkPlanPurchase/SingleNetworkSelection/components/device-compatibility";
import { TypeOfNumber } from "./pages/Customer/NetworkPlanPurchase/SingleNetworkSelection/components/type-of-number";
import { MultiNetworkSelection } from "./pages/Customer/NetworkPlanPurchase/MultiNetworkSelection/Loadable";
import { SelectionAndCoverage } from "./pages/Customer/NetworkPlanPurchase/MultiNetworkSelection/components/selection-and-coverage";
import { PhoneType } from "./pages/Customer/NetworkPlanPurchase/MultiNetworkSelection/components/phone-type";
import { NumberType } from "./pages/Customer/NetworkPlanPurchase/MultiNetworkSelection/components/number-type";
import { NumberInformation } from "./pages/Customer/NetworkPlanPurchase/MultiNetworkSelection/components/number-information";
import { Checkout } from "./pages/Customer/Checkout/Loadable";
import { MyAccountLayout } from "./components/AdminLayout/MyAccountLayout";
import { Dashboard } from "./pages/Customer/MyAccount/Dashboard/Loadable";
import { PaymentHistory } from "./pages/Customer/MyAccount/PaymentHistory/Loadable";
import { ManageProfile } from "./pages/Customer/MyAccount/ManageProfile/Loadable";
import { ManageCards } from "./pages/Customer/MyAccount/ManageCards/Loadable";
import { ChangePassword } from "./pages/Customer/MyAccount/ChangePassword/Loadable";
import { SimService } from "./pages/Customer/MyAccount/SimService/Loadable";
import { Login as AgentLogin } from "./pages/Agent/Login/Loadable";
import { CustomerDashboard } from "./pages/Agent/CustomerDashboard/Loadable";
import { TwoFactorAuthentication } from "./pages/Agent/TwoFactorAuthentication/Loadable";
import { SimUnlockRequest } from "./pages/Agent/SimUnlockRequest/Loadable";
import { SwapData } from "./pages/Agent/SwapData/Loadable";
import { SwapVoice } from "./pages/Agent/SwapVoice/Loadable";
import { NumberChange } from "./pages/Agent/NumberChange/Loadable";
import { UpdateProfile } from "./pages/Agent/UpdateProfile/Loadable";
import { UpdatePaymentMethod } from "./pages/Agent/UpdatePaymentMethod/Loadable";
import { UpgradePlan } from "./pages/Agent/UpgradePlan/Loadable";
import { AddLine } from "./pages/Agent/AddLine/Loadable";
import { CreateSupportTicket } from "./pages/Agent/CreateSupportTicket/Loadable";
import { NotFoundPage } from "./pages/NotFoundPage/Loadable";

/**
 * Pages that live in this SPA for local development.
 * On deployed hosts, blank immediately and redirect to the marketing site;
 * on localhost, render the in-app page.
 */
const LocalDevOrExternalPage = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const location = useLocation();
  const isLocal = isLocalDevHost();

  React.useLayoutEffect(() => {
    if (isLocal) return;
    redirectToAbsoluteUrl(
      `${location.pathname}${location.search}${location.hash}`,
    );
  }, [isLocal, location.pathname, location.search, location.hash]);

  if (isLocal) {
    return <>{children}</>;
  }

  // Cover AdminLayout chrome until the marketing host takes over.
  return (
    <div
      aria-busy="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2147483647,
        background: "#ffffff",
      }}
    />
  );
};

function ExternalNavigationGuard({
  children,
}: {
  children: React.ReactNode;
}) {
  React.useEffect(() => installExternalAnchorRedirectGuard(), []);
  return <>{children}</>;
}

export function App() {
  const { i18n } = useTranslation();
  useGlobalSlice();

  return (
    <ThemeProvider defaultTheme="light" storageKey="vle-theme">
      <RecaptchaProvider siteKey={recaptchaSiteKey}>
        <TooltipProvider>
          <DevAccessGateDialog>
            <BrowserRouter>
              <ExternalNavigationGuard>
                <TawkToHandler />
                <Helmet
                  titleTemplate="%s - VLE Mobile"
                  defaultTitle="VLE Mobile"
                  htmlAttributes={{ lang: i18n.language }}
                >
                  <meta name="description" content="VLE Mobile" />
                </Helmet>
                <Routes>
                  <Route element={<AdminLayout />}>
                    <Route
                      path="/"
                      element={
                        <LocalDevOrExternalPage>
                          <Home />
                        </LocalDevOrExternalPage>
                      }
                    />
                    <Route
                      path="/platform-drivers"
                      element={<PlatformDrivers />}
                    />
                    <Route path="/pricing" element={<PlansAndPricing />} />
                    <Route path="/how-to-switch" element={<HowToSwitch />} />
                    <Route path="/about-us" element={<AboutVLEMobile />} />
                    <Route
                      path="/how-vle-mobile-works"
                      element={<HowVLEMobileWorks />}
                    />
                    <Route
                      path="/faq"
                      element={
                        <LocalDevOrExternalPage>
                          <FAQ />
                        </LocalDevOrExternalPage>
                      }
                    />
                    <Route
                      path="/resource-center"
                      element={
                        <LocalDevOrExternalPage>
                          <ResourceCenter />
                        </LocalDevOrExternalPage>
                      }
                    />
                    <Route
                      path="/blog"
                      element={
                        <LocalDevOrExternalPage>
                          <Blog />
                        </LocalDevOrExternalPage>
                      }
                    />
                    <Route
                      path="/blog-detail"
                      element={
                        <LocalDevOrExternalPage>
                          <BlogDetail />
                        </LocalDevOrExternalPage>
                      }
                    />
                    <Route
                      path="/support-center"
                      element={
                        <LocalDevOrExternalPage>
                          <SupportCenter />
                        </LocalDevOrExternalPage>
                      }
                    />
                    <Route
                      path="/support-tickets"
                      element={
                        <LocalDevOrExternalPage>
                          <SupportTickets />
                        </LocalDevOrExternalPage>
                      }
                    />
                    <Route
                      path="/support-tickets/submit-ticket"
                      element={<SubmitTicket />}
                    />
                    <Route
                      path="/support-tickets/manage-ticket"
                      element={
                        <LocalDevOrExternalPage>
                          <ManageTicket />
                        </LocalDevOrExternalPage>
                      }
                    />
                    <Route
                      path="/why-vle-mobile"
                      element={<WhyChooseVLEMobile />}
                    />
                    <Route path="/fixed-phone" element={<FixedPhone />} />
                    <Route path="/fixed-phone/list-page" element={<ListPage />} />
                    <Route
                      path="/fixed-phone/phone-base"
                      element={<PhoneBase />}
                    />
                    <Route path="/activation" element={<Activation />} />
                    <Route element={<FixedPhoneFlow />}>
                      <Route
                        path="/activation/fixed-phone/activation-code"
                        element={<ActivationCode />}
                      />
                      <Route
                        path="/activation/fixed-phone/choose-service-plan"
                        element={<ChooseServicePlan />}
                      />
                      <Route
                        path="/activation/fixed-phone/security-plan-free-trial"
                        element={<SecurityPlanFreeTrial />}
                      />
                      <Route
                        path="/activation/fixed-phone/one-time-offer"
                        element={<OneTimeOffer />}
                      />
                    </Route>
                    <Route element={<PreloadedSimFlow />}>
                      <Route
                        path="/activation/preloaded-sim/device-info"
                        element={<DeviceInformation />}
                      />
                      <Route
                        path="/activation/preloaded-sim/choose-service-plan"
                        element={<SimCardUpsellChooseServicePlan />}
                      />
                      <Route
                        path="/activation/preloaded-sim/security-plan-upgrade"
                        element={<SecurityPlanUpgrade />}
                      />
                    </Route>
                    <Route path="/quick-recharge" element={<QuickRecharge />} />
                    <Route path="/schedule-call" element={<ScheduleCall />} />
                    <Route
                      path="/network-coverage"
                      element={<NetworkCoverage />}
                    />
                    <Route
                      path="/international-calling"
                      element={<InternationalCalls />}
                    />
                    <Route path="/imei-check" element={<IMEICheck />} />
                    <Route path="/about-esim" element={<AbouteSIM />} />
                    <Route
                      path="/single-network-coverage"
                      element={<DataTopup />}
                    />
                    <Route
                      path="/multi-network-coverage"
                      element={<DataTopup />}
                    />
                    <Route path="/family-plans" element={<FamilyPlans />} />
                    <Route path="/broadband-facts" element={<BroadbandFacts />} />
                    <Route
                      path="/digital-security"
                      element={<DigitalSecurity />}
                    />
                    <Route
                      path="/digital-security-selection"
                      element={<DigitalSecuritySelection />}
                    />
                    <Route path="/sim-swap-protection" element={<SIMSwap />} />
                    <Route path="/network-plan" element={<NetworkPlan />} />
                    <Route
                      path="/e911/911-emergency-services-statement"
                      element={<Disclosure />}
                    />
                    <Route
                      path="/network-management-policy"
                      element={<NetworkPolicy />}
                    />
                    <Route path="/accessibility" element={<Accessibility />} />
                    <Route path="/return-policy" element={<ReturnPolicy />} />
                    <Route
                      path="/terms-and-conditions"
                      element={<TermsAndConditions />}
                    />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route
                      path="/deletion-suspension-policy"
                      element={<DeletionPolicy />}
                    />
                    <Route path="/do-not-sell" element={<DoNotSellMyData />} />
                    <Route element={<SingleNetworkSelection />}>
                      <Route
                        path="/single-network-selection"
                        element={<MakeASelection />}
                      />
                      <Route
                        path="/single-network-phone-type"
                        element={<DeviceCompatibilty />}
                      />
                      <Route
                        path="/single-network-number-type"
                        element={<TypeOfNumber />}
                      />
                    </Route>
                    <Route element={<MultiNetworkSelection />}>
                      <Route
                        path="/multi-network-selection"
                        element={<SelectionAndCoverage />}
                      />
                      <Route
                        path="/multi-network-phone-type"
                        element={<PhoneType />}
                      />
                      <Route
                        path="/multi-network-number-type"
                        element={<NumberType />}
                      />
                      <Route
                        path="/multi-network-number-information"
                        element={<NumberInformation />}
                      />
                    </Route>
                    <Route element={<MyAccountLayout />}>
                      <Route
                        path="/my-account/dashboard"
                        element={<Dashboard />}
                      />
                      <Route
                        path="/my-account/payment-history"
                        element={<PaymentHistory />}
                      />
                      <Route
                        path="/my-account/manage-profile"
                        element={<ManageProfile />}
                      />
                      <Route
                        path="/my-account/manage-cards"
                        element={<ManageCards />}
                      />
                      <Route
                        path="/my-account/change-password"
                        element={<ChangePassword />}
                      />
                      <Route
                        path="/my-account/sim-service"
                        element={<SimService />}
                      />
                    </Route>
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/auth/exchange" element={<AuthExchange />} />
                    <Route
                      path="/agent/customer-dashboard"
                      element={<CustomerDashboard />}
                    />
                    <Route
                      path="/agent/two-factor-authentication"
                      element={<TwoFactorAuthentication />}
                    />
                    <Route
                      path="/agent/sim-unlock-request"
                      element={<SimUnlockRequest />}
                    />
                    <Route path="/agent/swap-data" element={<SwapData />} />
                    <Route path="/agent/swap-voice" element={<SwapVoice />} />
                    <Route
                      path="/agent/number-change"
                      element={<NumberChange />}
                    />
                    <Route
                      path="/agent/update-profile"
                      element={<UpdateProfile />}
                    />
                    <Route
                      path="/agent/update-payment-method"
                      element={<UpdatePaymentMethod />}
                    />
                    <Route path="/agent/upgrade-plan" element={<UpgradePlan />} />
                    <Route path="/agent/add-line" element={<AddLine />} />
                    <Route
                      path="/agent/create-support-ticket"
                      element={<CreateSupportTicket />}
                    />
                    <Route
                      path="*"
                      element={
                        <LocalDevOrExternalPage>
                          <NotFoundPage />
                        </LocalDevOrExternalPage>
                      }
                    />
                  </Route>
                  <Route path="/agent/login" element={<AgentLogin />} />
                </Routes>
              </ExternalNavigationGuard>
              {/* <GlobalStyle /> */}
            </BrowserRouter>
          </DevAccessGateDialog>
          <Toaster />
        </TooltipProvider>
      </RecaptchaProvider>
    </ThemeProvider>
  );
}
